import {
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { CalculsGlobal } from '@norma/shared/constants/calculs';
import { CalculPerteRevenusProche } from '@norma/shared/constants/calculs/calculPerteRevenusProche';
import { fCurrency } from '@norma/shared/helpers/formatNumber';
import { fAge, fYears } from '@norma/shared/helpers/formatTime';
import { getCapitalisationCoefficient } from '@norma/shared/helpers/prejudices/capitalisation';
import {
  Bareme,
  BaremeCapitalisationValue,
} from '@norma/shared/types/bareme.type';
import { PrejudiceFormPerteRevenusProche } from '@norma/shared/types/prejudice.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import { Victime } from '@norma/shared/types/victime.type';
import { VictimeIndirecte } from '@norma/shared/types/victimeIndirecte.type';
import React from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CoefficientUnavailableInBaremeTooltipText } from 'src/components/client/prejudiceFormComponents/Capitalisation/CoefficientUnavailableInBaremeTooltipText';
import { ComputedPropsForm } from 'src/components/forms/ComputedPropsForm';
import { ComputedReadFieldForm } from 'src/components/forms/ComputedReadFieldForm';
import { ComputedTextFieldForm } from 'src/components/forms/ComputedTextFieldForm';
import { ReadFieldForm } from 'src/components/forms/ReadFieldForm';
import { TextFieldForm } from 'src/components/forms/TextFieldForm';
import { IMaskNumberProps, MaskNumber } from 'src/components/masks/MaskNumber';
import { LayoutTable, NormalTable, SpanNoWrap } from 'src/components/styled';
import { PartialRecapitulatif } from '../PartialRecapitulatif';
interface Props {
  control: Control<PrejudiceFormPerteRevenusProche>;
  procedure: Procedure;
  victime: Victime;
  dateLiquidation: Date | undefined;
  baremes: Bareme[];
  victimesIndirectes: VictimeIndirecte[];
}

export const PrejudiceViagerFoyer: React.FC<Props> = ({
  control,
  procedure,
  victime,
  dateLiquidation,
  baremes,
  victimesIndirectes,
}) => {
  const { t } = useTranslation();
  const ageLiquidation = CalculsGlobal.getAgeOrZero(
    victime.dateNaissance || new Date(),
    procedure.dateLiquidation || new Date(),
  );
  const anneesEntreDecesEtLiquidation =
    victime.dateDeces && dateLiquidation
      ? CalculsGlobal.getYears(new Date(victime.dateDeces), dateLiquidation)
      : 0;
  const ageDateAttribution =
    victime.dateNaissance && dateLiquidation
      ? CalculsGlobal.getAge(new Date(victime.dateNaissance), dateLiquidation)
      : victime.dateNaissance
        ? CalculsGlobal.getAge(new Date(victime.dateNaissance), new Date())
        : undefined;
  const conjoint = victimesIndirectes.find(
    (victimeIndirecte) => victimeIndirecte.lienVictime === 'conjointConcubin',
  );
  const sexeConjoint = conjoint ? conjoint.sexe : null;
  const ageDateAttributionConjoint = conjoint
    ? CalculsGlobal.getAgeOrZero(
        conjoint?.dateNaissance,
        procedure.dateLiquidation || new Date(),
      )
    : null;

  return (
    <>
      <Stack
        sx={{ maxWidth: '700px' }}
        spacing={2}
        direction="row"
        marginTop={2}
      >
        <Typography>
          {t(
            'prejudice.prejudicesFormTypes.PERTES_REVENUS_PROCHE.victimeDecedee.fields.foyer.perteAnnuelle.tableRowHeader',
          )}
        </Typography>
        <ReadFieldForm
          control={control}
          name="foyer.perteAnnuelle"
          render={(value) => <SpanNoWrap>{fCurrency(value)}</SpanNoWrap>}
        />
      </Stack>
      <Typography variant="h6" marginTop={2}>
        {t(
          'prejudice.prejudicesFormTypes.PERTES_REVENUS_PROCHE.victimeDecedee.prejudiceViagerFoyer.entreDecesEtLiquidation.title',
        )}
      </Typography>
      <TableContainer sx={{ maxWidth: '700px' }}>
        <LayoutTable>
          <TableBody>
            <TableRow>
              <TableCell>
                {t(
                  'prejudice.prejudicesFormTypes.PERTES_REVENUS_PROCHE.victimeDecedee.fields.foyer.anneesEntreDecesEtLiquidation.tableRowHeader',
                )}
              </TableCell>
              <TableCell align="right">
                {fYears(anneesEntreDecesEtLiquidation)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t(
                  'prejudice.prejudicesFormTypes.PERTES_REVENUS_PROCHE.victimeDecedee.fields.foyer.prejudiceViagerEntreDecesEtLiquidation.tableRowHeader',
                )}
              </TableCell>
              <TableCell align="right">
                <ComputedReadFieldForm
                  control={control}
                  name="foyer.prejudiceViagerEntreDecesEtLiquidation"
                  watchFields={['foyer.perteAnnuelle']}
                  compute={([perteAnnuelle]) => {
                    return CalculPerteRevenusProche.getPrejudiceViagerFoyerEntreDecesEtLiquidation(
                      {
                        perteAnnuelleFoyer: perteAnnuelle,
                        anneesEntreDecesEtLiquidation,
                      },
                    );
                  }}
                  render={(value) => <>{fCurrency(value)}</>}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </LayoutTable>
      </TableContainer>
      <Typography variant="h6" marginTop={4}>
        {t(
          'prejudice.prejudicesFormTypes.PERTES_REVENUS_PROCHE.victimeDecedee.prejudiceViagerFoyer.apresLiquidation.title',
        )}
      </Typography>
      <TableContainer component={Paper}>
        <NormalTable size="medium">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="center">
                {t(
                  'prejudice.prejudicesFormTypes.PERTES_REVENUS_PROCHE.victimeDecedee.fields.agePaiement.columnHeader',
                )}
              </TableCell>
              <TableCell align="center">
                {t(
                  'prejudice.capitalisation.form.fields.coefficient.otherTitle',
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                {t(
                  'prejudice.prejudicesFormTypes.PERTES_REVENUS_PROCHE.victimeDecedee.fields.victime.title',
                )}
              </TableCell>
              <TableCell align="center">
                {ageLiquidation || ageLiquidation === 0
                  ? fAge(ageLiquidation)
                  : ''}
              </TableCell>
              <TableCell>
                <ComputedPropsForm
                  control={control}
                  watchFields={['baremeCapitalisation']}
                  compute={([baremeCapitalisation]) => {
                    const bareme = baremes.find(
                      (bareme) => bareme._id === baremeCapitalisation,
                    );
                    return {
                      hidden: bareme?.source === 'CJ',
                    };
                  }}
                  render={() => (
                    <ComputedTextFieldForm
                      control={control}
                      name="victime.coefficientCapitalisation"
                      watchFields={['baremeCapitalisation']}
                      compute={([baremeCapitalisation]) => {
                        const bareme: Bareme | undefined = baremes.find(
                          (bareme) => bareme._id === baremeCapitalisation,
                        );
                        if (bareme?.source === 'CJ') {
                          return undefined;
                        }
                        if (
                          bareme &&
                          victime.dateNaissance &&
                          dateLiquidation
                        ) {
                          return getCapitalisationCoefficient({
                            sexe: victime.sexe,
                            baremeValues:
                              bareme.values as BaremeCapitalisationValue[],
                            ageDateAttribution: ageDateAttribution || 0,
                            isDernierArrerageViager: true,
                            dateNaissance: victime.dateNaissance,
                            dateLiquidation,
                            ageDernierArrerage: null,
                          });
                        }
                        return null;
                      }}
                      computeTooltip={([baremeCapitalisation], fieldValue) => {
                        const bareme = baremes?.find(
                          (bareme) => bareme._id === baremeCapitalisation,
                        );
                        if (
                          !!bareme &&
                          bareme.source !== 'CJ' &&
                          !!victime.dateNaissance &&
                          !!(ageDateAttribution || ageDateAttribution === 0) &&
                          fieldValue === null
                        ) {
                          return <CoefficientUnavailableInBaremeTooltipText />;
                        }
                      }}
                      InputProps={{
                        inputComponent: MaskNumber as any,
                        inputProps: {
                          numberMask: { scale: 3 },
                          nullable: true,
                        } as IMaskNumberProps,
                      }}
                      fullWidth
                      editedFieldsName="editedFields"
                    />
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t(
                  'prejudice.prejudicesFormTypes.PERTES_REVENUS_PROCHE.victimeDecedee.fields.conjoint.title',
                )}
              </TableCell>
              <TableCell align="center">
                {ageDateAttributionConjoint || ageDateAttributionConjoint === 0
                  ? fAge(ageDateAttributionConjoint)
                  : null}
              </TableCell>
              <TableCell align="right">
                <ComputedPropsForm
                  control={control}
                  watchFields={['baremeCapitalisation']}
                  compute={([baremeCapitalisation]) => {
                    const bareme = baremes.find(
                      (bareme) => bareme._id === baremeCapitalisation,
                    );
                    return {
                      hidden: bareme?.source === 'CJ',
                    };
                  }}
                  render={() => (
                    <ComputedTextFieldForm
                      control={control}
                      name="conjoint.coefficientCapitalisation"
                      watchFields={['baremeCapitalisation']}
                      compute={([baremeCapitalisation]) => {
                        const bareme = baremes.find(
                          (bareme) => bareme._id === baremeCapitalisation,
                        );
                        if (bareme?.source === 'CJ') {
                          return undefined;
                        }
                        if (
                          bareme &&
                          conjoint?.dateNaissance &&
                          dateLiquidation &&
                          sexeConjoint &&
                          ageDateAttributionConjoint
                        ) {
                          return (
                            getCapitalisationCoefficient({
                              sexe: sexeConjoint,
                              baremeValues:
                                bareme.values as BaremeCapitalisationValue[],
                              ageDateAttribution:
                                ageDateAttributionConjoint.age,
                              isDernierArrerageViager: true,
                              ageDernierArrerage: null,
                              dateNaissance: conjoint?.dateNaissance,
                              dateLiquidation,
                            }) || 0
                          );
                        }
                        return 0;
                      }}
                      InputProps={{
                        inputComponent: MaskNumber as any,
                        inputProps: {
                          numberMask: { scale: 3 },
                        } as IMaskNumberProps,
                      }}
                      fullWidth
                      editedFieldsName="editedFields"
                    />
                  )}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </NormalTable>
      </TableContainer>
      <TableContainer sx={{ maxWidth: '700px' }}>
        <LayoutTable>
          <TableBody>
            <TableRow>
              <TableCell>
                {t(
                  'prejudice.prejudicesFormTypes.PERTES_REVENUS_PROCHE.victimeDecedee.fields.foyer.prejudiceViagerApresLiquidation.tableRowHeader',
                )}
              </TableCell>
              <TableCell align="right">
                <ComputedPropsForm
                  control={control}
                  watchFields={['baremeCapitalisation']}
                  compute={([baremeCapitalisation]) => {
                    const bareme = baremes.find(
                      (bareme) => bareme._id === baremeCapitalisation,
                    );
                    return {
                      props: {
                        isTextField: bareme?.source === 'CJ',
                      },
                    };
                  }}
                  render={({ isTextField }) =>
                    isTextField ? (
                      <TextFieldForm
                        control={control}
                        name="foyer.prejudiceViagerApresLiquidation"
                        InputProps={{
                          inputComponent: MaskNumber as any,
                          inputProps: {
                            numberMask: { scale: 2 },
                            suffix: '€',
                          } as IMaskNumberProps,
                        }}
                        fullWidth
                      />
                    ) : (
                      <ComputedTextFieldForm
                        control={control}
                        name="foyer.prejudiceViagerApresLiquidation"
                        watchFields={[
                          'foyer.perteAnnuelle',
                          'victime.coefficientCapitalisation',
                          'conjoint.coefficientCapitalisation',
                        ]}
                        compute={([
                          perteAnnuelle,
                          coefficientCapitalisationVictime,
                          coefficientCapitalisationConjoint,
                        ]) => {
                          return CalculPerteRevenusProche.getPrejudiceViagerFoyerApresLiquidation(
                            {
                              perteAnnuelleFoyer: perteAnnuelle,
                              coefficientCapitalisationVictime,
                              coefficientCapitalisationConjoint,
                            },
                          );
                        }}
                        InputProps={{
                          inputComponent: MaskNumber as any,
                          inputProps: {
                            numberMask: { scale: 2 },
                            suffix: '€',
                          } as IMaskNumberProps,
                        }}
                        editedFieldsName="editedFields"
                      />
                    )
                  }
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t(
                  'prejudice.prejudicesFormTypes.PERTES_REVENUS_PROCHE.victimeDecedee.fields.foyer.prejudiceViager.tableRowHeader',
                )}
              </TableCell>
              <TableCell align="right">
                <ComputedReadFieldForm
                  control={control}
                  name="foyer.prejudiceViager"
                  watchFields={[
                    'foyer.prejudiceViagerEntreDecesEtLiquidation',
                    'foyer.prejudiceViagerApresLiquidation',
                  ]}
                  compute={([
                    prejudiceViagerEntreDecesEtLiquidation,
                    prejudiceViagerApresLiquidation,
                  ]) => {
                    return CalculPerteRevenusProche.getPrejudiceViagerFoyer({
                      prejudiceViagerEntreDecesEtLiquidation,
                      prejudiceViagerApresLiquidation,
                    });
                  }}
                  render={(value) => <>{fCurrency(value)}</>}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </LayoutTable>
      </TableContainer>
      <ComputedPropsForm
        control={control}
        watchFields={[
          'foyer.prejudiceViagerEntreDecesEtLiquidation',
          'foyer.prejudiceViagerApresLiquidation',
        ]}
        compute={([
          prejudiceViagerEntreDecesEtLiquidation,
          prejudiceViagerApresLiquidation,
        ]) => {
          return {
            props: {
              echus: prejudiceViagerEntreDecesEtLiquidation,
              aEchoir: prejudiceViagerApresLiquidation,
              total:
                (prejudiceViagerEntreDecesEtLiquidation || 0) +
                (prejudiceViagerApresLiquidation || 0),
            },
          };
        }}
        render={({ echus, aEchoir, total }) => (
          <PartialRecapitulatif
            title={t(
              'prejudice.prejudicesFormTypes.PERTES_REVENUS_PROCHE.victimeDecedee.recapitulatif.partial.foyer.title',
            )}
            echus={echus}
            aEchoir={aEchoir}
            total={total}
          />
        )}
      />
    </>
  );
};
